import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import Guid from "_shared/guids/guid";
import { useGetProposalComparisonSummariesQuery } from "work/proposals/comparison/_api/proposal-comparison-api";

/** No sizes yet, or none to read. Module scope, so an empty answer is the same object every render. */
const NO_SIZES: ReadonlyMap<string, number> = new Map();

/**
 * How many proposals each of these request groups holds, for the groups this reader may compare
 * (#581). One read for the whole page, not one per row.
 *
 * The ids are the `RFPId`s of the rows, and the server sends a row its `RFPId` only when the reader
 * is on its client side, so a vendor's page asks about nothing. A group the server refuses or does
 * not know is simply not in the map: its rows offer no comparison.
 */
export default function useProposalComparisonSizes(
  rfpIds: readonly (string | undefined)[],
): ReadonlyMap<string, number> {
  // Sorted and joined, so the same groups in another row order do not read again.
  const key = [...new Set(rfpIds.filter((rfpId): rfpId is string => Boolean(rfpId) && rfpId !== Guid.EMPTY.value))].sort().join(",");
  const query = useGetProposalComparisonSummariesQuery(key ? { rfpIds: key.split(",") } : skipToken);
  const found = query.data?.found;
  return useMemo(
    () => (found ? new Map(found.map((summary) => [summary.id, summary.proposalCount])) : NO_SIZES),
    [found],
  );
}
